import React, { useState, useEffect, useRef } from "react";
import { Button, Collapse, Form, Container } from "react-bootstrap";
import "./filter.scss";

const Filter = ({ items, filter, onFilterChange }) => {
  const [statusOpen, setStatusOpen] = useState(false);
  const [issuerOpen, setIssuerOpen] = useState(false);

  // Local states for filter options
  const [status, setStatus] = useState(filter.status || []);
  const [issuers, setIssuers] = useState(filter.issuers || []);

  // Debounce filter updates to avoid excessive filtering
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      onFilterChange({
        status,
        issuers,
      });
    }, 300);
  }, [status, issuers, onFilterChange]);

  const handleStatusChange = (e) => {
    const { value, checked } = e.target;
    setStatus((prevStatus) =>
      checked ? [...prevStatus, value] : prevStatus.filter((item) => item !== value)
    );
  };

  const handleIssuerChange = (e) => {
    const { value, checked } = e.target;
    setIssuers((prevIssuers) =>
      checked ? [...prevIssuers, value] : prevIssuers.filter((issuer) => issuer !== value)
    );
  };

  const clearFilter = () => {
    setStatus([]);
    setIssuers([]);
    onFilterChange({
      status: [],
      issuers: [],
    });
  };

  // Build unique lists from the provided items.
  // If an item has a bpId property, use that for filtering.
  const uniqueIssuers = Array.from(
    new Set(
      items.map((item) =>
        item.bpId ? item.bpId : item.securityIssuer || item.issuerName || item.issuer
      )
    )
  );

  // Set the label dynamically based on whether bpId exists
  const filterLabel = items.length > 0 && items[0].bpId ? "BPID" : "Issuer";
  const uniqueStatuses = Array.from(new Set(items.map((item) => item.status)));

  return (
    <Container className="filter-container">
      <Button onClick={clearFilter} variant="outline-primary" className="mb-3">
        Clear Filter
      </Button>

      <Form>
        {/* Status Filter */}
        <Form.Group controlId="statusFilter" className="bordered-group">
          <Button
            onClick={() => setStatusOpen(!statusOpen)}
            aria-controls="status-collapse"
            aria-expanded={statusOpen}
            variant="outline-secondary"
            className="w-100 text-left no-border statusFilterButton"
          >
            Status
            <span className={`arrow ${statusOpen ? "up" : "down"}`}></span>
          </Button>
          <Collapse in={statusOpen}>
            <Container className={`collapseContainer ${statusOpen ? "expanded" : "collapsed"}`}>
              <div id="status-collapse" className="mt-2">
                {uniqueStatuses.map((statusItem, index) => (
                  <Form.Check
                    key={`status-${index}`}
                    id={`status-${index}`}
                    type="checkbox"
                    value={statusItem}
                    label={statusItem}
                    checked={status.includes(statusItem)}
                    onChange={handleStatusChange}
                  />
                ))}
              </div>
            </Container>
          </Collapse>
        </Form.Group>

        {/* Issuer/BP ID Filter */}
        <Form.Group controlId="issuerFilter" className="bordered-group">
          <Button
            onClick={() => setIssuerOpen(!issuerOpen)}
            aria-controls="issuer-collapse"
            aria-expanded={issuerOpen}
            variant="outline-secondary"
            className="w-100 text-left no-border statusFilterButton"
          >
            {filterLabel}
            <span className={`arrow ${issuerOpen ? "up" : "down"}`}></span>
          </Button>
          <Collapse in={issuerOpen}>
            <Container className={`collapseContainer ${issuerOpen ? "expanded" : "collapsed"}`}>
              <div id="issuer-collapse" className="mt-2">
                {uniqueIssuers.map((issuer, index) => (
                  <Form.Check
                    key={`issuer-${index}`}
                    id={`issuer-${index}`}
                    type="checkbox"
                    value={issuer}
                    label={issuer}
                    checked={issuers.includes(issuer)}
                    onChange={handleIssuerChange}
                  />
                ))}
              </div>
            </Container>
          </Collapse>
        </Form.Group>
      </Form>
    </Container>
  );
};

export default Filter;
